const PASTE_BLOCK_TAGS = new Set([
    'P', 'DIV', 'SECTION', 'ARTICLE', 'HEADER', 'FOOTER', 'MAIN', 'ASIDE', 'NAV',
    'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'PRE', 'FIGURE', 'FIGCAPTION', 'ADDRESS',
    'TABLE', 'THEAD', 'TBODY', 'TFOOT', 'TR', 'DL', 'DT', 'DD',
]);

export function escapeHtml(value) {
    if (typeof value !== 'string') return '';
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#x27;');
}

export function sanitizePastedHtml(html) {
    if (!html || typeof html !== 'string') return '';

    const doc = new DOMParser().parseFromString(html, 'text/html');

    // Remove non-content & dangerous elements
    doc.body
        .querySelectorAll(
            'script,style,link,meta,title,svg,picture,iframe,object,embed,video,audio,form,input,button,select,textarea,canvas,map,noscript'
        )
        .forEach((el) => el.remove());

    const cleanChildren = (node) => {
        let out = '';
        node.childNodes.forEach((child) => {
            if (child.nodeType === Node.TEXT_NODE) {
                out += escapeHtml(child.nodeValue || '');
            } else if (child.nodeType === Node.ELEMENT_NODE) {
                out += cleanElement(child);
            }
        });
        return out;
    };

    const cleanElement = (el) => {
        const tag = el.tagName;

        if (tag === 'BR') return '<br>';
        if (tag === 'HR') return '<div><br></div>';

        let content = cleanChildren(el);

        // Extract formatting from tags AND inline CSS (Google Docs, Word, Apple Notes)
        const style = el.style || {};
        const fontWeight = style.fontWeight || '';
        const decoration = `${style.textDecoration || ''} ${style.textDecorationLine || ''}`;

        const explicitlyNotBold = /^(normal|[1-4]00)$/.test(fontWeight);
        const bold = !explicitlyNotBold &&
            (tag === 'B' || tag === 'STRONG' || /^(bold|bolder|[6-9]00)$/.test(fontWeight));
        const italic = tag === 'I' || tag === 'EM' || style.fontStyle === 'italic';
        const underline = tag === 'U' || decoration.includes('underline');
        const strike = tag === 'S' || tag === 'STRIKE' || tag === 'DEL' || decoration.includes('line-through');

        if (bold) content = `<b>${content}</b>`;
        if (italic) content = `<i>${content}</i>`;
        if (underline) content = `<u>${content}</u>`;
        if (strike) content = `<s>${content}</s>`;

        switch (tag) {
            case 'A': {
                const href = (el.getAttribute('href') || '').trim();
                if (/^(https?:|mailto:|tel:)/i.test(href)) {
                    return `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${content}</a>`;
                }
                return content;
            }
            case 'IMG': {
                const src = (el.getAttribute('src') || '').trim();
                if (/^(https?:|data:image\/|blob:)/i.test(src)) {
                    const alt = escapeHtml(el.getAttribute('alt') || 'Blog Image');
                    return `<img src="${escapeHtml(src)}" alt="${alt}" class="rte-inline-image" />`;
                }
                return '';
            }
            case 'H1': return `<h1>${content}</h1>`;
            case 'H2': return `<h2>${content}</h2>`;
            case 'H3': return `<h3>${content}</h3>`;
            case 'H4': return `<h4>${content}</h4>`;
            case 'H5': return `<h5>${content}</h5>`;
            case 'H6': return `<h6>${content}</h6>`;
            case 'UL': return `<ul>${content}</ul>`;
            case 'OL': return `<ol>${content}</ol>`;
            case 'LI': return `<li>${content}</li>`;
            case 'BLOCKQUOTE': return `<blockquote>${content}</blockquote>`;
            case 'TD':
            case 'TH':
                return content ? `${content} ` : '';
            default:
                if (PASTE_BLOCK_TAGS.has(tag)) {
                    return content ? `<div>${content}</div>` : '';
                }
                return content;
        }
    };

    return cleanChildren(doc.body);
}
